import { type App, type WritableComputedRef } from "vue";
import { createI18n, type Composer, type I18n } from "vue-i18n"
import { configureRouter, useRouter } from "../routing-services";
import { type RouteLocationRaw } from "vue-router";
import { defaultsDeep } from "lodash";

export interface I18nServiceConfiguration {
    app: App,
    defaultLocale: string,
    supportedLocales: string[],
    fallbackLocales: string[],
    logWarns: boolean,
    localeSelectorFallbackMode?: LocaleSelectFallbackMode | ((locale: string, cfg: I18nServiceConfiguration, i18n: I18nService ) => string),
    buildCallback?: (i18n: I18n) => Promise<void>
    onLocaleChanged?: (locale: string) => Promise<void>
}

export type LocaleSelectFallbackMode = "None" | "Language" | "LanguageIgnoreRegion";

export class I18nService {
    private _cfg: I18nServiceConfiguration;
    private _availableLocales: Set<string>;
    private _loadedLocales: Set<string>;
    private _i18n: any;


    constructor(cfg: I18nServiceConfiguration, i18n: I18n) {
        this._cfg = cfg;
        this._availableLocales = new Set(cfg.supportedLocales);
        this._i18n = i18n;
        this._loadedLocales = new Set();
        
    }

    isLocaleSupported(locale: string) {
        return this._availableLocales.has(locale);
    }


    defaultLocaleFallbackSelector(locale: string, mode?: LocaleSelectFallbackMode) {

        if (locale == null || locale.length == 0)
            return null;

        if (this.isLocaleSupported(locale))
            return locale;

        if (mode !== "None") {
            const parts = locale.split("-");
            for (let i = parts.length - 1; i > 0; i--) {
                const lang = parts.slice(0, i).join("-");
                if (this.isLocaleSupported(lang))
                    return lang;
            }

            if (mode !== "Language") {
                return this._cfg.supportedLocales.find(l => l.startsWith(parts[0])) ?? null;
            }
        }
    }

    getMostSpecificSupportedLocale(locale: string | null) {
        if (locale == null)
            return null;

        if (typeof this._cfg.localeSelectorFallbackMode === "function")
            return this._cfg.localeSelectorFallbackMode(locale, this._cfg, this);
        return this.defaultLocaleFallbackSelector(locale, this._cfg.localeSelectorFallbackMode);
    }

    getCurrentLocale() {
        return this._i18n.global.locale.value;
    }

    getUserLocale() {
        return window.navigator.language || this._cfg.defaultLocale;
    }

    getPersistedLocale() {
        return localStorage.getItem("locale");
    }

    guessDefaultLocale() {
        return this.getMostSpecificSupportedLocale(this.getPersistedLocale())
            || this.getMostSpecificSupportedLocale(this.getUserLocale())
            || this._cfg.defaultLocale;
    }

    public i18Route(route : RouteLocationRaw, locale?: string) {
        let newRoute : RouteLocationRaw;
        if (locale == null)
            locale = this.getCurrentLocale();

        if (typeof route === "string") 
            newRoute = {path: `/${locale}${route}`, params: {}} as RouteLocationRaw;
        /*else if (route.path != null) {
            newRoute = {...route, path: `/${locale}${route.path}`} as RouteLocationRaw;
        }*/
        else {
            const resolved = useRouter().resolve(route);
            if (resolved.params?.locale)
                return route;
            newRoute = {...route, ...{path: `/${locale}${resolved.path}`, name: null}};
        }
        return newRoute;
    }
    
    async setLocale(locale: string) {

        if (locale === this.getCurrentLocale())
            return;
        
        await this.loadLocaleMessages(locale);
    
        if (this._i18n.mode === "legacy")
            this._i18n.global.locale = locale;
        else
            (this._i18n.global.locale as WritableComputedRef<string>).value = locale;

                    
        if (this._cfg.onLocaleChanged)
            await this._cfg.onLocaleChanged(this.getCurrentLocale());

        document.querySelector("html")?.setAttribute("lang", locale);
    }

    async switchLanguage(locale: string) {
        await this.setLocale(locale);


        localStorage.setItem("locale", locale);
    }

    public async loadLocaleMessages(locale: string) {
        if (!this.isLocaleLoaded(locale)) {
            const sharedLocale = (await import(`./../../locales/${locale}.json`)).default;
            const appLocale = (await import(`./../../../locales/${locale}.json`)).default;
            const computedLocale = defaultsDeep({}, sharedLocale, appLocale);
            this._i18n.global.setLocaleMessage(locale, computedLocale);
            this._loadedLocales.add(locale);
        }
    }

    private isLocaleLoaded(locale: string) {
        return this._loadedLocales.has(locale);
    }

    

    get i18n() : Composer<{},{},{},string,never,string> {
        return this._i18n.global;
    }

}

let instance: I18nService;
let i18n: I18n;


function useIntl() {
    return instance;
}

async function configureI18n(cfg: I18nServiceConfiguration) {

    i18n = createI18n({
        legacy: false,
        fallbackLocale: cfg.fallbackLocales,
        availableLocales: [],
        missingWarn: cfg.logWarns ?? true,
        fallbackWarn: cfg.logWarns ?? true,

    });

    if (cfg.buildCallback)
        await cfg.buildCallback(i18n);


    cfg.app.use(i18n);
    instance = new I18nService(cfg, i18n);

    for (const locale of cfg.fallbackLocales) {
        await instance.loadLocaleMessages(locale);
    }

    await instance.setLocale(instance.guessDefaultLocale());

    configureRouter(b => {

        b.setDefaultParent("__localized");

        b.addRoute("__localized", b => b.withPath("/:locale?").allowAnonymous().isRoot().configure(r => {
            r.beforeEnter = async (to, from, next) => {
                const paramLocale = to.params.locale as string;
                const locale = instance.getMostSpecificSupportedLocale(paramLocale);
  
                if (locale == null) {
                    const guessedLocale = instance.guessDefaultLocale();
                    return next(instance.i18Route(to, guessedLocale));
                }
                else if (locale !== paramLocale) {
                    return next(locale);
                }

                await instance.setLocale(locale);
                return next();
            }
        }));
    });
}

export {
    configureI18n,
    useIntl
}